import React, { useMemo } from 'react';
import { useSurvey } from '../../../../admin-page/apis/useSurvey';
import SurveyForm from '../../../../SurveyForm';
import { useQueryClient } from '@tanstack/react-query';
import { ADMIN_PROGRESS_QUERY_KEY } from '../../../useAdminProgressQuery';

const addChooseNotToAnswer = (options) => [
  ...options,
  {
    value: 'choose_not_to_answer',
    label: 'I choose not to answer',
  },
];

const ezParentLabel = (
  <>
    <i>ez</i>Parent
  </>
);

const ezTrainingLabel = (
  <>
    <i>ez</i>Training
  </>
);

export default function PostTrainingSurvey({ closeModal }) {
  const queryClient = useQueryClient();
  const mutation = useSurvey({
    url: '/api/survey/adminpost',
  });
  const agreementOptions = useMemo(
    () =>
      addChooseNotToAnswer([
        { value: 'strongly_disagree', label: 'Strongly disagree' },
        { value: 'disagree', label: 'Disagree' },
        { value: 'agree', label: 'Agree' },
        { value: 'strongly_agree', label: 'Strongly agree' },
      ]),
    [],
  );

  const confidenceOptions = useMemo(
    () =>
      addChooseNotToAnswer([
        { value: 'not_at_all_confident', label: 'Not at all confident' },
        { value: 'slightly_confident', label: 'Slightly confident' },
        { value: 'moderately_confident', label: 'Moderately confident' },
        { value: 'very_confident', label: 'Very confident' },
      ]),
    [],
  );

  const questions = useMemo(
    () => [
      {
        name: 'understand_strategies',
        type: 'single',
        ariaLabel:
          'After completing ezTraining, I understand the strategies and principles used in the ezParent Program.',
        label: (
          <>
            After completing {ezTrainingLabel}, I understand the strategies and
            principles used in the {ezParentLabel} Program.
          </>
        ),
        required: true,
        options: agreementOptions,
      },
      {
        name: 'understand_facilitation',
        type: 'single',
        ariaLabel:
          'After completing ezTraining, I understand how to effectively facilitate ezParent groups.',
        label: (
          <>
            After completing {ezTrainingLabel}, I understand how to effectively
            facilitate {ezParentLabel} groups.
          </>
        ),
        required: true,
        options: agreementOptions,
      },
      {
        name: 'content_clarity',
        type: 'single',
        ariaLabel:
          'The information presented across the four ezTraining modules was clear and well organized.',
        label: (
          <>
            The information presented across the four {ezTrainingLabel} modules{' '}
            was clear and well organized.
          </>
        ),
        required: true,
        options: agreementOptions,
      },
      {
        name: 'confidence_after_training',
        type: 'single',
        ariaLabel:
          'After completing the ezTraining, how confident do you feel about facilitating ezParent groups?',
        label: (
          <>
            After completing the {ezTrainingLabel}, how confident do you feel
            about facilitating {ezParentLabel} groups?
          </>
        ),
        required: true,
        options: confidenceOptions,
      },
      {
        name: 'additional_feedback',
        type: 'textarea',
        label: (
          <>
            Is there any other feedback you would like to share about the{' '}
            {ezTrainingLabel} modules?
          </>
        ),
        rows: 5,
        placeholder: 'Share any additional feedback',
      },
    ],
    [agreementOptions, confidenceOptions],
  );

  const handleSubmit = async (responses) => {
    await mutation.mutateAsync(responses);
    await queryClient.invalidateQueries({
      queryKey: ADMIN_PROGRESS_QUERY_KEY,
    });
    closeModal();
  };

  return (
    <SurveyForm
      title={<>Post {ezTrainingLabel} Survey</>}
      description={
        <>
          Thank you for completing the {ezTrainingLabel} modules. We appreciate
          your time and would value your feedback on your experience.
        </>
      }
      questions={questions}
      onSubmit={handleSubmit}
      submitLabel='Submit'
      submitOptionLabels
      defaultValues={{
        understand_strategies: '',
        understand_facilitation: '',
        content_clarity: '',
        confidence_after_training: '',
        additional_feedback: '',
      }}
    />
  );
}
